import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { User, Briefcase, Loader2 } from 'lucide-react';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../Services/apiService';
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { validatePhoneRealTime } from '../utils/phoneValidation';

const AddParticipantModal = ({ open, onClose, searchedEmail, onUserCreated }) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(currentUser?.role === 'employee' ? 'vendor' : 'employee');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phone validation states
  const [selectedCountry, setSelectedCountry] = useState('in');
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [vendorPhoneValidation, setVendorPhoneValidation] = useState({ isValid: true, message: '' });
  const [vendorPhoneTouched, setVendorPhoneTouched] = useState(false);
  const [vendorSelectedCountry, setVendorSelectedCountry] = useState('in');

  // Employee form state
  const [employeeData, setEmployeeData] = useState({
    full_name: '',
    email: searchedEmail || '',
    phone_number: '',
    role: 'employee'
  });

  // Vendor form state
  const [vendorData, setVendorData] = useState({
    full_name: '',
    email: searchedEmail || '',
    phone_number: '',
    role: 'vendor'
  });

  // Update email when searchedEmail changes
  React.useEffect(() => {
    if (searchedEmail) {
      setEmployeeData(prev => ({ ...prev, email: searchedEmail }));
      setVendorData(prev => ({ ...prev, email: searchedEmail }));
    }
  }, [searchedEmail]);

  // Validation functions
  const validateEmail = (email) => {
    if (!email || !email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone || !phone.trim()) return 'Phone number is required';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10) return 'Phone must be exactly 10 digits';
    if (!['6', '7', '8', '9'].includes(cleaned[0])) return 'Phone must start with 6, 7, 8, or 9';
    return '';
  };

  const validateName = (name) => {
    if (!name || !name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    return '';
  };

  const handleEmployeeSubmit = async () => {
    // Validate
    const nameError = validateName(employeeData.full_name);
    const emailError = validateEmail(employeeData.email);

    // Validate phone
    const formPhone = employeeData.phone_number.startsWith('+') ? employeeData.phone_number : `+${employeeData.phone_number}`;
    const phoneValidationResult = validatePhoneRealTime(formPhone, selectedCountry?.toUpperCase());

    if (nameError || emailError) {
      showToast.error(nameError || emailError);
      return;
    }

    if (!phoneValidationResult.isValid) {
      setPhoneValidation(phoneValidationResult);
      setPhoneTouched(true);
      showToast.error(phoneValidationResult.message);
      return;
    }

    setIsSubmitting(true);
    try {
      const formPhone = employeeData.phone_number.startsWith('+') ? employeeData.phone_number : `+${employeeData.phone_number}`;
      const payload = {
        ...employeeData,
        full_name: employeeData.full_name.trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '), // Title case
        phone_number: formPhone,
      };

      const response = await userAPI.createUser(payload);
      const newUser = response.data;

      showToast.success(`Employee "${newUser.full_name}" created successfully`);
      onUserCreated(newUser);
      handleClose();
    } catch (error) {
      handleAPIError(error, 'Failed to create employee', true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmployeePhoneChange = (value, country, metadata) => {
    setEmployeeData(prev => ({ ...prev, phone_number: value }));
    setPhoneTouched(true);

    if (country?.countryCode) {
      setSelectedCountry(country.countryCode.toLowerCase());
    }

    const countryCode = country?.countryCode || selectedCountry?.toUpperCase() || null;
    const validation = validatePhoneRealTime(value, countryCode);
    setPhoneValidation(validation);
  };

  const handleVendorPhoneChange = (value, country, metadata) => {
    setVendorData(prev => ({ ...prev, phone_number: value }));
    setVendorPhoneTouched(true);

    if (country?.countryCode) {
      setVendorSelectedCountry(country.countryCode.toLowerCase());
    }

    const countryCode = country?.countryCode || vendorSelectedCountry?.toUpperCase() || null;
    const validation = validatePhoneRealTime(value, countryCode);
    setVendorPhoneValidation(validation);
  };

  const handleVendorSubmit = async () => {
    // Validate
    const nameError = validateName(vendorData.full_name);
    const emailError = validateEmail(vendorData.email);

    // Validate phone
    const formPhone = vendorData.phone_number.startsWith('+') ? vendorData.phone_number : `+${vendorData.phone_number}`;
    const phoneValidationResult = validatePhoneRealTime(formPhone, vendorSelectedCountry?.toUpperCase());

    if (nameError || emailError) {
      showToast.error(nameError || emailError);
      return;
    }

    if (!phoneValidationResult.isValid) {
      setVendorPhoneValidation(phoneValidationResult);
      setVendorPhoneTouched(true);
      showToast.error(phoneValidationResult.message);
      return;
    }

    setIsSubmitting(true);
    try {
      const formPhone = vendorData.phone_number.startsWith('+') ? vendorData.phone_number : `+${vendorData.phone_number}`;
      const payload = {
        ...vendorData,
        full_name: vendorData.full_name.trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '), // Title case
        phone_number: formPhone,
      };

      const response = await userAPI.createUser(payload);
      const newUser = response.data;

      showToast.success(`Vendor "${newUser.full_name}" created successfully`);
      onUserCreated(newUser);
      handleClose();
    } catch (error) {
      handleAPIError(error, 'Failed to create vendor', true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmployeeData({ full_name: '', email: searchedEmail || '', phone_number: '', role: 'employee' });
    setVendorData({ full_name: '', email: searchedEmail || '', phone_number: '', role: 'vendor' });
    setActiveTab(currentUser?.role === 'employee' ? 'vendor' : 'employee');

    // Reset validation states
    setPhoneValidation({ isValid: true, message: '' });
    setPhoneTouched(false);
    setSelectedCountry('in');
    setVendorPhoneValidation({ isValid: true, message: '' });
    setVendorPhoneTouched(false);
    setVendorSelectedCountry('in');

    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] h-[70%] flex flex-col p-0">
        <div className="px-6 pt-4">
          <DialogHeader>
            <DialogTitle>Add New Participant</DialogTitle>

          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="">
            {currentUser?.role === 'employee' ? (
              // Employee: Only Vendor tab
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Create Vendor
                </h3>
              </div>
            ) : (
              // Admin/Business Head: Both tabs
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="employee" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Employee
                </TabsTrigger>
                <TabsTrigger value="vendor" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Vendor
                </TabsTrigger>
              </TabsList>
            )}

            {/* Employee Form - Only show if not employee role */}
            {currentUser?.role !== 'employee' && (
              <TabsContent value="employee" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <div className="space-y-0">
                    <Label htmlFor="emp-name">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="emp-name"
                      placeholder="Enter Full Name"
                      value={employeeData.full_name}
                      onChange={(e) => setEmployeeData(prev => ({ ...prev, full_name: e.target.value }))}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-0">
                    <Label htmlFor="emp-email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="emp-email"
                      type="email"
                      value={employeeData.email}
                      onChange={(e) => setEmployeeData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={isSubmitting}
                      className="bg-gray-50"
                    />
                    {/* <p className="text-xs text-gray-500">Only @gmail.com emails allowed</p> */}
                  </div>

                  <div className="space-y-0 flex gap-1 flex-col w-full">
                    <label htmlFor="emp-phone" className="text-sm font-medium">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="w-full">
                      <PhoneInput
                        id="emp-phone"
                        defaultCountry="in"
                        value={employeeData.phone_number}
                        onChange={handleEmployeePhoneChange}
                        forceDialCode={true}
                        className='w-full'
                        inputStyle={{ width: "100%", height: "36px" }}
                        hideDropdown={false}
                        disableCountryGuess={false}
                        disabled={isSubmitting}
                      />
                    </div>
                    {/* Real-time validation feedback */}
                    {phoneTouched && (
                      <div className="mt-1 space-y-1">
                        {phoneValidation.isValid && (
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-green-600 font-medium">
                              {phoneValidation.message}
                            </p>
                          </div>
                        )}
                        {!phoneValidation.isValid && (
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-red-600 font-medium">
                              {phoneValidation.message}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-0">
                    <Label>Role</Label>
                    <Input value="Employee" disabled className="bg-gray-100" />
                  </div>
                </div>
              </TabsContent>
            )}

            {/* Vendor Form */}
            <TabsContent value="vendor" className="space-y-4 mt-4">
              <div className="space-y-2">
                <div className="space-y-0">
                  <Label htmlFor="vend-name">Vendor Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="vend-name"
                    placeholder="Enter Vendor Name"
                    value={vendorData.full_name}
                    onChange={(e) => setVendorData(prev => ({ ...prev, full_name: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="space-y-0">
                  <Label htmlFor="vend-email">Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="vend-email"
                    type="email"
                    value={vendorData.email}
                    onChange={(e) => setVendorData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={isSubmitting}
                    className="bg-gray-50"
                  />
                  {/* <p className="text-xs text-gray-500">Only @gmail.com emails allowed</p> */}
                </div>

                <div className="space-y-0 flex gap-1 flex-col w-full">
                  <label htmlFor="vend-phone" className="text-sm font-medium">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="w-full">
                    <PhoneInput
                      id="vend-phone"
                      defaultCountry="in"
                      value={vendorData.phone_number}
                      onChange={handleVendorPhoneChange}
                      forceDialCode={true}
                      className='w-full'
                      inputStyle={{ width: "100%", height: "36px" }}
                      hideDropdown={false}
                      disableCountryGuess={false}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* Real-time validation feedback */}
                  {vendorPhoneTouched && (
                    <div className="mt-1 space-y-1">
                      {vendorPhoneValidation.isValid && (
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-green-600 font-medium">
                            {vendorPhoneValidation.message}
                          </p>
                        </div>
                      )}
                      {!vendorPhoneValidation.isValid && (
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-red-600 font-medium">
                            {vendorPhoneValidation.message}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-0">
                  <Label>Role</Label>
                  <Input value="Vendor" disabled className="bg-gray-100" />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Vendors are only visible in meeting participant lists. They won't appear in user management or other modules.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>


        <div className="sticky top-0 bg-white border-t px-6 py-4 flex justify-end gap-3 ">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={currentUser?.role === 'employee' ? handleVendorSubmit : (activeTab === 'employee' ? handleEmployeeSubmit : handleVendorSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create & Add'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddParticipantModal;

